import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { GoalDtoIn } from '@/models/in/GoalDtoIn';
import { ConvertSupportedRequestToFundExpenseResultDtoIn } from '@/models/in/ConvertSupportedRequestToFundExpenseResultDtoIn';
import { ConvertSupportedRequestToFundExpenseDtoOut } from '@/models/out/ConvertSupportedRequestToFundExpenseDtoOut';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { FUND_EXPENSE_GOAL_NAMES, isFundraisingExpenseGoalName } from '@/constants/fundExpenseGoals';
import { INVOICE_NUMBER_MAX_LENGTH } from '@/pages/newSupportRequest/schemas/supportRequestFormSchema';
import styles from './ConvertToFundExpenseDialog.module.css';

interface ConvertToFundExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  request: ManagementSupportRequestDtoIn;
  onSuccess: () => void;
}

const ConvertToFundExpenseDialog = ({ open, onClose, request, onSuccess }: ConvertToFundExpenseDialogProps) => {
  const { t, i18n } = useTranslation();
  const beneficiary = request.beneficiaryContact;
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const { data: fundExpenseGoals, isLoading: isLoadingGoals } = useGetAuthApi<GoalDtoIn[]>(
    open && request.fundId ? `funds/${request.fundId}/fund-expense-goals` : ''
  );

  const selectableGoals = useMemo(() => fundExpenseGoals ?? [], [fundExpenseGoals]);

  const { mutate: convertRequest, isPending: isSubmitting } = usePostAuthApi<
    ConvertSupportedRequestToFundExpenseResultDtoIn,
    ConvertSupportedRequestToFundExpenseDtoOut
  >(`management/support-requests/${request.multiRequestCorrelationId}/convert-to-fund-expense`);

  useEffect(() => {
    if (open) {
      setSelectedGoalId(null);
      setComment('');
      setInvoiceNumber('');
      setError(null);
      setResultMessage(null);
    }
  }, [open]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' });

  const supplierPreviewName = beneficiary
    ? `${beneficiary.firstName ?? ''} ${beneficiary.lastName ?? ''}`.trim()
    : '';

  const handleSubmit = () => {
    if (!selectedGoalId) {
      setError(t('convertToFundExpense.selectGoalRequired'));
      return;
    }
    if (!beneficiary?.israeliId) {
      setError(t('convertToFundExpense.missingIsraeliId'));
      return;
    }
    const trimmedInvoiceNumber = invoiceNumber.trim();
    if (!trimmedInvoiceNumber) {
      setError(t('convertToFundExpense.missingInvoiceNumber'));
      return;
    }

    setError(null);
    setResultMessage(null);

    const body: ConvertSupportedRequestToFundExpenseDtoOut = {
      correlationId: request.multiRequestCorrelationId,
      newGoalTypeId: selectedGoalId,
      comment: comment.trim() || undefined,
      invoiceNumber: trimmedInvoiceNumber,
    };

    convertRequest(body, {
      onSuccess: (result) => {
        // Always refetch the parent list immediately so it reflects the converted request,
        // regardless of whether we keep the dialog open to show an exceptions-committee
        // notice. (Previously the refetch was delayed until the user dismissed the notice,
        // which left the table stale - see code-review bug #10.)
        onSuccess();

        if (result.movedToExceptionsCommittee) {
          const baseMessage = t('convertToFundExpense.successMessage', {
            count: result.updatedCount,
            supplierName: result.supplierName,
          });
          const eligible = result.fundraisingEligibleAmount;
          const exceptionsMessage =
            eligible != null && eligible !== Number.MAX_VALUE
              ? t('convertToFundExpense.movedToExceptionsWithAmount', { amount: formatCurrency(eligible) })
              : t('convertToFundExpense.movedToExceptions');
          setResultMessage(`${baseMessage} ${exceptionsMessage}`);
        } else {
          // No exceptions-committee notice to show - close the dialog. The parent table
          // is already refetching above.
          onClose();
        }
      },
      onError: (err) => {
        const axiosError = err as { response?: { data?: string } };
        const serverMessage = axiosError.response?.data || err.message;
        setError(t('convertToFundExpense.errorConverting') + ': ' + serverMessage);
      },
    });
  };

  const handleConfirmAndClose = () => {
    // onSuccess already fired when the mutation resolved; just dismiss the notice dialog.
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('convertToFundExpense.title')}
    >
      <div className={styles.content}>
        <p className={styles.intro}>{t('convertToFundExpense.intro')}</p>

        <div className={styles.previewBox}>
          <h4 className={styles.previewTitle}>{t('convertToFundExpense.supplierPreview')}</h4>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>{t('supplierName')}:</span>
            <span className={styles.previewValue}>{supplierPreviewName || '-'}</span>
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>{t('supplierTaxId')}:</span>
            <span className={styles.previewValue}>{beneficiary?.israeliId || '-'}</span>
          </div>
          {beneficiary?.phone && (
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>{t('phoneNumber')}:</span>
              <span className={styles.previewValue}>{beneficiary.phone}</span>
            </div>
          )}
          {beneficiary?.email && (
            <div className={styles.previewRow}>
              <span className={styles.previewLabel}>{t('email')}:</span>
              <span className={styles.previewValue}>{beneficiary.email}</span>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <label className={styles.label}>{t('convertToFundExpense.selectClassification')}</label>
          {isLoadingGoals ? (
            <p className={styles.hint}>{t('loading')}...</p>
          ) : (
            <div className={styles.radioGroup}>
              {selectableGoals.map((goal) => (
                <label key={goal.id} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="convertGoalType"
                    value={goal.id}
                    checked={selectedGoalId === goal.id}
                    onChange={() => setSelectedGoalId(goal.id)}
                    disabled={!!resultMessage}
                  />
                  <span>
                    {t(goal.name)}
                    {goal.name === FUND_EXPENSE_GOAL_NAMES.FUNDRAISING && (
                      <span className={styles.subtitleHint}> ({t('convertToFundExpense.fundraisingHint')})</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <label htmlFor="convert-invoice-number" className={styles.label}>
            {t('invoiceNumber')}
          </label>
          <input
            id="convert-invoice-number"
            type="text"
            maxLength={INVOICE_NUMBER_MAX_LENGTH}
            className={styles.input}
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder={t('invoiceNumberPlaceholder') || ''}
            disabled={!!resultMessage}
          />
        </div>

        <div className={styles.section}>
          <label htmlFor="convert-comment" className={styles.label}>
            {t('note')}
          </label>
          <textarea
            id="convert-comment"
            className={styles.textarea}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!!resultMessage}
            rows={3}
          />
        </div>

        {isFundraisingExpenseGoalName(selectableGoals.find((g) => g.id === selectedGoalId)?.name) && (
          <p className={styles.warning}>{t('convertToFundExpense.fundraisingWarning')}</p>
        )}

        {error && <p className={styles.error}>{error}</p>}
        {resultMessage && <p className={styles.warning}>{resultMessage}</p>}

        <div className={styles.actions}>
          {resultMessage ? (
            <Button variant="primary" onClick={handleConfirmAndClose}>
              {t('close')}
            </Button>
          ) : (
            <>
              <Button variant="white" onClick={onClose} disabled={isSubmitting}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoadingGoals || !selectedGoalId || !invoiceNumber.trim()}
              >
                {isSubmitting ? t('loading') : t('convertToFundExpense.confirm')}
              </Button>
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ConvertToFundExpenseDialog;
